/**
 * Разбор сообщения: контекст, переписка и ответ.
 *
 * Ответ отправляется тем же экраном, где видно, на что отвечаешь: переключаться
 * между списком и формой ради двух строк — лишняя работа. Сообщение при этом
 * само переходит в «отвечено», иначе очередь новых разъезжается с тем, что
 * модератор уже разобрал.
 */
import { useState } from "react";
import { Show } from "@refinedev/antd";
import { useInvalidate, useShow } from "@refinedev/core";
import { Button, Card, Descriptions, Form, Input, Select, Space, Typography, message } from "antd";
import { Link } from "react-router";

import { request } from "../../lib/http";
import { FEEDBACK_STATUSES, kindLabel, statusTag } from "../../lib/feedback";
import { formatDateTime } from "../../lib/constants";
import type { FeedbackItem } from "./list";

export function FeedbackShow() {
  const { query } = useShow<FeedbackItem>({ resource: "feedback" });
  const record = query.data?.data;
  const invalidate = useInvalidate();

  const [form] = Form.useForm();
  const [sending, setSending] = useState(false);

  const refresh = () => {
    void invalidate({ resource: "feedback", invalidates: ["list", "detail"], id: record?.id });
  };

  // Ответ и смена статуса идут через собственный http-клиент, а не через
  // useCustomMutation: он ходит в метод custom data-provider'а, которого у
  // написанного руками провайдера нет, и запрос уходил в никуда.
  const reply = async (values: { message: string }) => {
    if (!record?.id) return;

    setSending(true);
    try {
      await request(`/api/v1/admin/feedback/${record.id}/reply`, {
        method: "POST",
        body: { message: values.message },
      });
      message.success("Ответ отправлен");
      form.resetFields();
      refresh();
    } catch (error) {
      message.error(error instanceof Error ? error.message : "Не удалось отправить ответ");
    } finally {
      setSending(false);
    }
  };

  const changeStatus = async (status: string) => {
    if (!record?.id) return;

    try {
      await request(`/api/v1/admin/feedback/${record.id}/status`, {
        method: "PUT",
        body: { status },
      });
      message.success("Статус изменён");
      refresh();
    } catch (error) {
      message.error(error instanceof Error ? error.message : "Не удалось изменить статус");
    }
  };

  return (
    <Show title="Сообщение" isLoading={query.isLoading} headerButtons={<></>}>
      <Descriptions column={2} bordered size="small">
        <Descriptions.Item label="Когда">{formatDateTime(record?.createdAt)}</Descriptions.Item>
        <Descriptions.Item label="От кого">{record?.author?.username ?? "—"}</Descriptions.Item>
        <Descriptions.Item label="Тип">{kindLabel(record?.kind)}</Descriptions.Item>
        <Descriptions.Item label="Уточнение">{record?.topic || "—"}</Descriptions.Item>
        <Descriptions.Item label="Статус">
          <Space>
            {statusTag(record?.status)}
            <Select
              size="small"
              style={{ width: 150 }}
              value={record?.status}
              options={FEEDBACK_STATUSES.map((item) => ({ value: item.value, label: item.label }))}
              onChange={(value) => void changeStatus(value)}
            />
          </Space>
        </Descriptions.Item>
        <Descriptions.Item label="Контекст">
          {/* Ссылки на то, о чём речь: искать кадр по описанию — лишняя работа. */}
          <Space direction="vertical" size={0}>
            {record?.questId && (
              <Link to={`/quests/show/${record.questId}`}>{record.questTitle || "Квест"}</Link>
            )}
            {record?.photoId && <Link to={`/photos/show/${record.photoId}`}>Кадр</Link>}
            {!record?.questId && !record?.photoId && "—"}
          </Space>
        </Descriptions.Item>
      </Descriptions>

      <Card size="small" title="Сообщение" style={{ marginTop: 16 }}>
        <Typography.Paragraph style={{ whiteSpace: "pre-line", marginBottom: 0 }}>
          {record?.message}
        </Typography.Paragraph>
      </Card>

      {(record?.replies ?? []).length > 0 && (
        <Card size="small" title="Переписка" style={{ marginTop: 16 }}>
          <Space direction="vertical" style={{ width: "100%" }}>
            {record?.replies?.map((item) => (
              <Typography.Paragraph key={item.id} style={{ whiteSpace: "pre-line", marginBottom: 8 }}>
                {item.message}
              </Typography.Paragraph>
            ))}
          </Space>
        </Card>
      )}

      <Card size="small" title="Ответить" style={{ marginTop: 16 }}>
        <Form form={form} onFinish={(values) => void reply(values as { message: string })}>
          <Form.Item
            name="message"
            rules={[{ required: true, message: "Ответ не может быть пустым" }]}
            // Ответ увидит игрок в своём профиле — это не служебная заметка.
            extra="Игрок увидит ответ в своём профиле, во вкладке «Сообщения»"
          >
            <Input.TextArea rows={4} placeholder="Спасибо, поправили" />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={sending}>
            Отправить
          </Button>
        </Form>
      </Card>
    </Show>
  );
}
